// src/components/ResultsPage.js
import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import "./ResultsPage.css";
import { searchBusinessApi, getCID, getSubID } from "../services/ApiAndLongServices";

function applyTrackingParams(url, cid, subid) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (!u.searchParams.has("utm_source") && cid) u.searchParams.set("utm_source", cid);
    if (cid) u.searchParams.set("utm_content", cid);
    if (subid) u.searchParams.set("utm_subid", subid);
    return u.toString();
  } catch {
    return url;
  }
}
/* ---------- simple card ---------- */
const Card = ({ title, msg, onBack }) => (
  <div className="container">
    <div className="card">
      <h2 className="title">{title}</h2>
      {msg && <p>{msg}</p>}
      {onBack && (
        <button className="button" onClick={onBack}>
          Go Back
        </button>
      )}
    </div>
  </div>
);

/* ---------- description that truncates ---------- */
function Description({ text = "", collapsedLines = 3, onReadMore }) {
  const pRef = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (pRef.current) {
      setTruncated(pRef.current.scrollHeight > pRef.current.clientHeight + 2);
    }
  }, [text]);

  return (
    <div style={{ width: "100%" }}>
      <p
        ref={pRef}
        className="desc-text desc-text--clamp"
        style={{ WebkitLineClamp: collapsedLines }}
      >
        {text}
      </p>
      {truncated && (
        <span
          className="see-more"
          role="button"
          tabIndex={0}
          onClick={onReadMore}
        >
          Read more
        </span>
      )}
    </div>
  );
}

/* ---------- pro details modal (NO X) ---------- */
function DetailModal({ pro, onClose, onQuote }) {
  if (!pro) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* removed all X buttons */}
        <div className="modal-pro-content">
          <h3 className="pro-name">{pro.business_name || pro.name}</h3>
          {pro.rating && (
            <div className="pro-rating">
              ★ {pro.rating.toFixed(1)}
              {typeof pro.num_reviews !== "undefined" && (
                <span> ({pro.num_reviews} reviews)</span>
              )}
            </div>
          )}
          <div className="modal-description">
            {pro.introduction || pro.description}
          </div>
          <button
            className="button"
            style={{ marginTop: 28 }}
            onClick={() => {
              onClose();
              onQuote(pro.thumbtack_url);
            }}
          >
            Get quote
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------- iframe modal (NO X) ---------- */
function QuoteModal({ url, onClose, cid, subid }) {
  const finalUrl = applyTrackingParams(url, cid, subid);
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  useEffect(() => {
    const onMsg = (e) => {
      const t = e?.data?.type || e?.data;
      if (
        t === "widget:close" ||
        t === "redo:close" ||
        t === "thumbtack:close"
      ) {
        onClose();
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [onClose]);

  if (!finalUrl) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <iframe
          src={finalUrl}
          title="Get quote"
          className="modal-iframe"
        />
      </div>
    </div>
  );
}
/* ==================================================================== */
/*                               PAGE                                   */
/* ==================================================================== */
export default function ResultsPage() {
  const nav = useNavigate();
  const { state } = useLocation();

  const zipCode = state?.zipCode;
  const categoryId = state?.categoryId;
  const campaign = state?.campaign;

  const trustedFormCertUrl = state?.trustedFormCertUrl;

  const [pros, setPros] = useState([]);
  const [load, setLoad] = useState(true);
  const [err, setErr] = useState(null);
  const [locationName, setLocationName] = useState("");

  const [detailModal, setDetailModal] = useState(null);
  const [quoteModalUrl, setQuoteModalUrl] = useState(null);


  useEffect(() => {
    document.body.style.overflow = detailModal || quoteModalUrl ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [detailModal, quoteModalUrl]);

  // Resolve location
  useEffect(() => {
    if (!zipCode) return;
    fetch(`https://api.zippopotam.us/us/${zipCode}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d =>
        d?.places?.length
          ? setLocationName(`${d.places[0]["place name"]}, ${d.places[0]["state abbreviation"]}`)
          : setLocationName("")
      )
      .catch(() => setLocationName(""));
  }, [zipCode]);

  // Fetch pros
  useEffect(() => {
    if (!zipCode || !categoryId || !campaign) return;

    (async () => {
      try {
        const payload = await searchBusinessApi(zipCode, categoryId, trustedFormCertUrl, getCID(), getSubID(), campaign);

        const adapt = p => ({
          service_id: p.serviceID || p.service_id,
          id: p.businessID || p.id,
          business_name: p.businessName || p.displayName || p.name,
          rating: p.rating ?? p.averageRating,
          num_reviews: p.num_reviews ?? p.reviewCount ?? p.numberOfReviews,
          introduction: p.introduction ?? p.businessIntroduction ??
            p.intro ?? p.description,
          description: p.description,
          thumbtack_url: p.widgets?.requestFlowURL || p.widgets?.servicePageURL ||
            p.servicePageURL || p.thumbtack_url || p.url,
        });

        const list = payload.data || payload.businesses || payload.results || [];
        setPros(Array.isArray(list) ? list.map(adapt) : []);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoad(false);
      }
    })();
  }, [zipCode, categoryId, campaign]);

  return (
    <>
      <Header />

      <DetailModal
        pro={detailModal}
        onClose={() => setDetailModal(null)}
        onQuote={url => setQuoteModalUrl(url)}
      />

      <QuoteModal
        url={quoteModalUrl}
        cid={getCID()}
        subid={getSubID()}
        onClose={() => setQuoteModalUrl(null)}
      />

      {!zipCode || !categoryId || !campaign ? (
        <Card
          title="Missing info"
          msg="ZIP code and category are required."
          onBack={() => nav("/")}
        />
      ) : load ? (
        <Card title="Loading…" />
      ) : err ? (
        <Card title="Error" msg={err} onBack={() => nav("/")} />
      ) : !pros.length ? (
        <Card
          title="No professionals found"
          msg={`None in ${zipCode}.`}
          onBack={() => nav("/")}
        />
      ) : (
        <div className={detailModal || quoteModalUrl ? "modal-blur-root" : ""}>
          <div className="results-container">
            <h2 className="results-title">
              Available professionals in {locationName || zipCode}
            </h2>

            <div className="pros-grid">
              {pros.map((p, idx) => (
                <div
                  key={`sid-${p.service_id || "x"}-id-${p.id || "y"}-${idx}`}
                  className="pro-card"
                >
                  <h3 className="pro-name">{p.business_name}</h3>

                  {p.rating && (
                    <div className="pro-rating">
                      ★ {p.rating.toFixed(1)}{" "}
                      {typeof p.num_reviews !== "undefined" && (
                        <span>({p.num_reviews} reviews)</span>
                      )}
                    </div>
                  )}

                  {(p.introduction || p.description) && (
                    <Description
                      text={p.introduction || p.description}
                      onReadMore={() => setDetailModal(p)}
                    />
                  )}

                  <button
                    className="button"
                    onClick={() => setQuoteModalUrl(p.thumbtack_url)}
                  >
                    Get quote
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </>
  );
}
